/**
 * The marketing site's hero panel, rebuilt here.
 *
 * alchemiq.ai carries no illustrations at all — the brand book bans them
 * (§7: "avoid gradients, illustrations, decorative textures") — and its one
 * piece of hero imagery is this: a live list of what the engine has actually
 * detected in the last 24 hours. So "add an image from the website" resolves
 * to showing the product working, which is also the only honest option.
 *
 * Same public endpoint the site uses (`/api/stories`, unauthenticated), the
 * same white card on a hairline border, the same pulsing orange dot.
 *
 * WHERE THIS DELIBERATELY DIFFERS FROM THE SITE: the site falls back to a
 * hard-coded list of stories when the fetch fails. This renders NOTHING.
 * A campaign page captioned "detected by Alchemiq" showing invented
 * headlines is precisely the reconstruction PRD §3 forbids, and an empty
 * column is a much smaller cost than a fabricated one.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const ENDPOINT_LIMIT = 20;
const SHOWN = 6;

/** "8m", "3h" — the site's own compact form. */
export function relativeTime(publishedAt, now = Date.now()) {
  const then = new Date(publishedAt).getTime();
  if (!Number.isFinite(then)) return null;
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

export function sourceLabel(sources) {
  const names = (sources || []).map((s) => s?.name).filter(Boolean);
  if (names.length === 0) return null;
  if (names.length <= 3) return names.join(' · ');
  return `${names.slice(0, 3).join(' · ')} · +${names.length - 3}`;
}

export default function LiveStoriesPanel({ onHasStories }) {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    let live = true;
    const base = process.env.REACT_APP_BASE_URL || '/api';
    axios
      .get(`${base}/stories`, { params: { category: 'all', hours: 24, limit: ENDPOINT_LIMIT } })
      .then(({ data }) => {
        if (!live || !Array.isArray(data)) return;
        const usable = data.filter((s) => s && s.title).slice(0, SHOWN);
        setStories(usable);
        // The caller collapses its layout when this is empty, rather than
        // holding a 420px column open for a panel that never appears.
        if (typeof onHasStories === 'function') onHasStories(usable.length > 0);
      })
      // Silent: this is decoration on a page whose real job is the brief box,
      // and an error notice about a side panel would be noise.
      .catch(() => {
        if (live && typeof onHasStories === 'function') onHasStories(false);
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stories.length === 0) return null;

  return (
    <section className="cmp-live" aria-label="Recently detected by Alchemiq">
      <header className="cmp-live-head">
        <span className="cmp-live-dot" aria-hidden />
        Emerging now · Detected by Alchemiq
      </header>
      <ul className="cmp-live-list">
        {stories.map((story) => {
          const when = relativeTime(story.publishedAt);
          const sources = sourceLabel(story.sources);
          return (
            <li className="cmp-live-row" key={story.title}>
              {when && <span className="cmp-live-when">{when}</span>}
              <div className="cmp-live-body">
                <p className="cmp-live-title">{story.title}</p>
                {sources && <p className="cmp-live-sources">{sources}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
